import { useQuery } from "@apollo/client";
import {
  Button,
  CheckIcon,
  Container,
  Group,
  Paper,
  Stack,
  Stepper,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBrandTiktok, IconMail, IconUser } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GET_USER } from "../../../apis/lambda/GraphQL/User/queries";
import TikTokLoginButton from "../../../components/TikTokLoginButton/TikTokLoginButton";
import { decodeJWTFromCookie } from "../../../utils/jwtUtils";

function SignupCompletePage() {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const location = useLocation();
  const email: string | undefined = location.state?.email;

  // React state
  const [active, setActive] = useState(0);

  // GraphQL
  const { data, loading } = useQuery(GET_USER, {
    variables: {
      email: decodeJWTFromCookie()?.email ?? "",
    },
    fetchPolicy: "network-only",
  });

  const user = data?.user;

  const isEmailVerified = user?.validated === true;
  const isProfileComplete = !!(user?.name && user?.description);
  const isTiktokLinked = !!user?.tiktokData;

  const iconStep1 = isEmailVerified ? (
    <CheckIcon size="1.1rem" />
  ) : (
    <IconMail size="1.1rem" />
  );
  const iconStep2 = isProfileComplete ? (
    <CheckIcon size="1.1rem" />
  ) : (
    <IconUser size="1.1rem" />
  );
  const iconStep3 = isTiktokLinked ? (
    <CheckIcon size="1.1rem" />
  ) : (
    <IconBrandTiktok size="1.1rem" />
  );

  useEffect(() => {
    if (loading) return;

    if (!isEmailVerified) {
      setActive(0);
    } else if (!isProfileComplete) {
      setActive(1);
    } else if (!isTiktokLinked) {
      setActive(2);
    }
  }, [loading, isEmailVerified, isProfileComplete, isTiktokLinked]);

  const allStepsComplete =
    isEmailVerified && isProfileComplete && isTiktokLinked;

  return (
    <Container size="md" py="xl">
      <Paper p="xl" radius="lg" shadow="md" withBorder>
        <Stack align="center" gap="lg">
          <Title order={2}>Welcome! Let's get you ready for brands.</Title>
          <Text c="dimmed" ta="center">
            Complete these steps to go live and start collaborating.
          </Text>

          <Stepper
            active={active}
            onStepClick={setActive}
            allowNextStepsSelect
            orientation={isMobile ? "vertical" : "horizontal"}
          >
            <Stepper.Step
              label="Step 1"
              description="Verify your email"
              icon={iconStep1}
              completedIcon={iconStep1}
            >
              <Text size="sm" c="dimmed" mt="md" ta="center">
                We've sent a confirmation link to{" "}
                <strong>{email || user?.email || "your email"}</strong>. Please
                check your inbox.
              </Text>
            </Stepper.Step>
            <Stepper.Step
              label="Step 2"
              description="Complete your profile"
              icon={<IconUser size="1.1rem" />}
            >
              <Text size="sm" c="dimmed" mt="md" ta="center">
                A complete profile helps brands find you.
              </Text>
              <Group justify="center" mt="md">
                <Button component={Link} to="/profile">
                  Go to Profile
                </Button>
              </Group>
            </Stepper.Step>
            <Stepper.Step
              label="Step 3"
              description="Link your TikTok (Recommended)"
              icon={<IconBrandTiktok size="1.1rem" />}
            >
              <Text size="sm" c="dimmed" mt="md" ta="center">
                Connect your TikTok to show brands your engagement and reach.
              </Text>
              <Group justify="center" mt="md">
                <TikTokLoginButton />
              </Group>
            </Stepper.Step>
          </Stepper>

          <Button component={Link} to="/discover" variant="subtle" mt="xl">
            {allStepsComplete ? "Go to App" : "Skip for now"}
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
}

export default SignupCompletePage;
